import { CheckCircle2, Download } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { useExam } from '../context/ExamContext';
import {
  CANDIDATE_REPORT_FILENAME,
  createCandidateReportElement,
  getReportSecondsRemaining,
  isCurrentSessionReport,
  revokeTemporaryReportUrl
} from '../utils/candidateReport';
import { exportReportPdf } from '../utils/exportReportPdf';

export function FinishedPage() {
  const { state } = useExam();
  const temporaryUrlRef = useRef<string>();
  const [remaining, setRemaining] = useState(() => getReportSecondsRemaining(state.reportExpiresAt));
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string>();
  const isPublicDemo = state.candidate?.mode === 'public-demo';
  const canDownload = isPublicDemo && isCurrentSessionReport(state) && remaining > 0;

  useEffect(() => {
    const update = () => {
      const next = getReportSecondsRemaining(state.reportExpiresAt);
      setRemaining(next);
      if (next === 0) {
        revokeTemporaryReportUrl(temporaryUrlRef.current);
        temporaryUrlRef.current = undefined;
      }
    };
    update();
    const timer = window.setInterval(update, 250);
    return () => {
      window.clearInterval(timer);
      revokeTemporaryReportUrl(temporaryUrlRef.current);
      temporaryUrlRef.current = undefined;
    };
  }, [state.reportExpiresAt]);

  async function downloadReport() {
    if (!canDownload) return;
    const report = createCandidateReportElement(state);
    document.body.appendChild(report);
    setExporting(true);
    setExportError(undefined);
    try {
      await exportReportPdf(report, CANDIDATE_REPORT_FILENAME);
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'PDF export failed.');
    } finally {
      report.remove();
      setExporting(false);
    }
  }

  const minutes = Math.floor(remaining / 60).toString().padStart(2, '0');
  const seconds = (remaining % 60).toString().padStart(2, '0');

  return (
    <section className="mx-auto max-w-2xl px-4 py-16 text-center">
      <CheckCircle2 className="mx-auto h-14 w-14 text-mint" />
      <p className="mt-4 text-sm font-semibold uppercase text-neon-blue">Prototype Demo</p>
      <h1 className="mt-2 text-3xl font-black text-white">Exam Finished</h1>
      <p className="mt-3 text-slate-300">Your exam was submitted successfully.</p>

      {isPublicDemo && remaining > 0 ? (
        <div className="mt-7 flex flex-col items-center gap-3">
          <p className="text-sm text-slate-300">Your temporary proctoring report is ready.</p>
          <p role="timer" className="font-mono text-sm text-slate-200">Report download available for {minutes}:{seconds}</p>
          <Button loading={exporting} icon={<Download className="h-4 w-4" />} onClick={() => void downloadReport()}>
            {exporting ? 'Preparing Report' : 'Download Proctoring Report'}
          </Button>
          {exportError ? <p role="alert" className="text-sm text-red-300">{exportError}</p> : null}
        </div>
      ) : isPublicDemo ? (
        <p role="status" className="mt-7 text-sm text-amber-200">The temporary report download window has expired.</p>
      ) : null}

      <div className="mt-8 flex justify-center"><Link to="/"><Button variant="secondary">Return Home</Button></Link></div>
    </section>
  );
}
